import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { API, auth, fmtDate, inRange, exportExcel, printArea, PrintStyle, exportPdf } from '../shared';
import KpiCard from '../KpiCard';
import SimpleBarList from '../SimpleBarList';
import ReportActions from '../ReportActions';
import { SectionCard, EmptyState } from '../../../../components/dashboard/TodaySchedule';
import { getReportSettings, logExport } from '../../../../lib/reportStudioApi';
import { useClubIdentity } from '../../../../lib/useClubIdentity';
import ReportPrintFrame from '../../../../components/reportStudio/ReportPrintFrame';

const STATUS_LABELS = { draft: 'مسودة', active: 'نشط', archived: 'مؤرشف' };
const TYPE_LABELS = { strategy: 'استراتيجية', policy: 'سياسة', objective: 'هدف' };
const getUserName = () => { try { return JSON.parse(localStorage.getItem('user') || 'null')?.full_name; } catch { return null; } };

export default function GovernanceTab({ filters }) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [board, setBoard] = useState([]);
  const [activity, setActivity] = useState([]);
  const [boardDenied, setBoardDenied] = useState(false);
  const [studio, setStudio] = useState(null);
  const identity = useClubIdentity();
  const printRef = useRef(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [i, b, a, st] = await Promise.allSettled([
        axios.get(`${API}/governance-items`, auth()),
        axios.get(`${API}/board-members`, auth()),
        axios.get(`${API}/activity-log`, auth()),
        getReportSettings('governance_report'),
      ]);
      setItems(i.status === 'fulfilled' ? i.value.data : []);
      if (b.status === 'fulfilled') { setBoard(b.value.data?.members || []); setBoardDenied(false); } else { setBoardDenied(true); }
      setActivity(a.status === 'fulfilled' ? a.value.data : []);
      setStudio(st.status === 'fulfilled' ? st.value : null);
      setLoading(false);
    })();
  }, []);

  if (loading) {
    return <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">{[1, 2, 3, 4].map((i) => <div key={i} className="h-24 animate-pulse rounded-xl bg-slate-100" />)}</div>;
  }

  const settings = studio?.effective || {};
  const content = settings.content || {};
  const templateUsed = studio?.use_custom ? 'تخصيص مستقل' : null;

  const periodActivity = activity.filter((a) => inRange(a.created_at, filters.date_from, filters.date_to));
  const byStatus = {};
  items.forEach((it) => { byStatus[it.status] = (byStatus[it.status] || 0) + 1; });
  const rows = items.map((it) => ({ 'النوع': TYPE_LABELS[it.item_type] || it.item_type, 'العنوان': it.title, 'الحالة': STATUS_LABELS[it.status] || it.status, 'المسؤول': it.owner || '—' }));

  const handlePdf = async () => {
    try {
      await exportPdf(printRef.current, 'تقرير-الحوكمة', { orientation: settings.page?.orientation, paperSize: settings.page?.paper_size });
      logExport('governance_report', 'pdf', templateUsed, 'success', 'تقرير-الحوكمة.pdf');
    } catch { logExport('governance_report', 'pdf', templateUsed, 'failed', null); }
  };
  const handleExcel = () => {
    exportExcel(rows, 'تقرير-الحوكمة');
    logExport('governance_report', 'excel', templateUsed, 'success', 'تقرير-الحوكمة.xlsx');
  };

  return (
    <div className="space-y-4" data-testid="governance-tab">
      <PrintStyle />
      <div className="flex justify-end print:hidden">
        <ReportActions testId="governance-report" onExcel={handleExcel} onPdf={handlePdf} onPrint={printArea} disabled={rows.length === 0} />
      </div>
      <div className="print-area" ref={printRef}>
        <ReportPrintFrame settings={settings} identity={identity} reportTitle="تقرير الحوكمة والإدارة" exportedByName={getUserName()}>
          <div className="space-y-4">
            {content.stat_cards !== false && (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <KpiCard icon="flag" label="عناصر الحوكمة" value={items.length} testId="kpi-gov-items" />
                <KpiCard icon="building" label="أعضاء مجلس الإدارة" value={boardDenied ? '—' : board.length} testId="kpi-gov-board" />
                <KpiCard icon="clock" label="نشاطات الفترة" value={periodActivity.length} testId="kpi-gov-activity" />
                <KpiCard icon="check" label="عناصر نشطة" value={byStatus.active || 0} accent="text-teal-700" testId="kpi-gov-active" />
              </div>
            )}

            {content.charts !== false && (
              <SectionCard title="توزيع عناصر الحوكمة حسب الحالة" icon="grid" testId="governance-distribution-card">
                {Object.keys(byStatus).length === 0
                  ? <EmptyState icon="flag" text="لا توجد عناصر حوكمة مسجلة" testId="governance-distribution-empty" />
                  : <SimpleBarList items={Object.entries(byStatus).map(([k, v]) => ({ label: STATUS_LABELS[k] || k, value: v }))} testId="governance-distribution-bars" />}
              </SectionCard>
            )}

            {content.tables !== false && (
              <SectionCard title="عناصر الحوكمة" icon="flag" testId="governance-items-card">
                {items.length === 0 ? <EmptyState icon="flag" text="لا توجد عناصر حوكمة مسجلة" testId="governance-items-empty" /> : (
                  <ul className="space-y-1 text-sm">{items.slice(0, 50).map((it) => (
                    <li key={it.id} className="flex items-center justify-between border-b border-slate-50 py-1.5" data-testid="governance-item-row">
                      <span>{it.title} <span className="text-xs text-slate-400">({TYPE_LABELS[it.item_type] || it.item_type})</span></span>
                      <span className="text-xs font-semibold text-slate-500">{STATUS_LABELS[it.status] || it.status}</span>
                    </li>
                  ))}</ul>
                )}
              </SectionCard>
            )}

            {content.tables !== false && (
              <SectionCard title="آخر النشاطات خلال الفترة" icon="clock" testId="governance-activity-card">
                {periodActivity.length === 0 ? <EmptyState icon="clock" text="لا توجد نشاطات مسجلة خلال هذه الفترة" testId="governance-activity-empty" /> : (
                  <ul className="space-y-1 text-sm">{periodActivity.slice(0, 30).map((a, idx) => (
                    <li key={idx} className="border-b border-slate-50 py-1" data-testid="governance-activity-row"><b>{a.action}</b> — {a.details} <span className="text-xs text-slate-400">({fmtDate(a.created_at)})</span></li>
                  ))}</ul>
                )}
              </SectionCard>
            )}
          </div>
        </ReportPrintFrame>
      </div>
    </div>
  );
}
